import { MAX_SHRINES } from "./respawn.ts";
import { MAX_LOCAL_SIXTEENTHS, PLACE_LIMIT_TILES, compareShrineOrder, worldDistance, worldPosition, type KnownShrine, type WorldPlace } from "./world.ts";

/**
 * The world's shrine index (ADR-106 decision 10): the compiled `server/shrines.json`, read by the gateway and by a map
 * server. Plain data, validated whole; a malformed index is refused with the field named and a reader never throws.
 */

export const SHRINE_INDEX_SCHEMA = 1;

/**
 * The most shrines a character knows elsewhere that a handoff carries (ADR-106 decision 12).
 */
export const MAX_KNOWN_ELSEWHERE = 1024;

export const MAX_INDEX_ID_CHARS = 64;
export const MAX_INDEX_NAME_CHARS = 48;

/**
 * A shrine in the index: its id on its map, name, ground point and standing point, in sixteenths of its own map.
 */
export interface ShrineIndexShrine {
  readonly id: string;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly standX: number;
  readonly standY: number;
}

/**
 * A map in the index: its id and display name, placement in whole tiles (null for an unplaced map), spawn in sixteenths
 * (null for none), the id of its starting shrine (null for none) and its shrines in id order.
 */
export interface ShrineIndexMap {
  readonly id: string;
  readonly name: string;
  readonly place: WorldPlace | null;
  readonly spawn: { readonly x: number; readonly y: number } | null;
  readonly start: string | null;
  readonly shrines: readonly ShrineIndexShrine[];
}

/**
 * The index: schema 1, the home map's id and every map of the world.
 */
export interface ShrineIndex {
  readonly schema: 1;
  readonly home: string;
  readonly maps: readonly ShrineIndexMap[];
}

/**
 * The result of reading an index: the index, or the error naming the field that was refused.
 */
export type ShrineIndexRead = { readonly ok: true; readonly index: ShrineIndex } | { readonly ok: false; readonly error: string };

/**
 * A character's row: a shrine it has discovered, by map and shrine id.
 */
export interface KnownRow {
  readonly mapId: string;
  readonly shrineId: string;
}

/**
 * A refusal of the index, carrying the message that names the field.
 */
class Refusal extends Error {}

/**
 * Refuses the read with a message naming a field.
 *
 * @param path The field's path.
 * @param rule What the field must be.
 * @returns Never.
 * @throws Refusal always.
 */
function refuse(path: string, rule: string): never {
  throw new Refusal(`${path}: ${rule}`);
}

/**
 * Reads a plain object.
 *
 * @param value The value.
 * @param path The field's path.
 * @returns The object.
 * @throws Refusal when the value is not a plain object.
 */
function asObject(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    refuse(path, "must be an object");
  }
  return value as Record<string, unknown>;
}

/**
 * Reads a string of a length range.
 *
 * @param value The value.
 * @param path The field's path.
 * @param min The fewest characters.
 * @param max The most characters.
 * @returns The string.
 * @throws Refusal when it is not a string of that length.
 */
function asString(value: unknown, path: string, min: number, max: number): string {
  if (typeof value !== "string" || value.length < min || value.length > max) {
    refuse(path, `must be a string of ${min} to ${max} characters`);
  }
  return value;
}

/**
 * Reads a whole number in a range.
 *
 * @param value The value.
 * @param path The field's path.
 * @param min The least.
 * @param max The greatest.
 * @returns The number.
 * @throws Refusal when it is not a whole number in range.
 */
function asWhole(value: unknown, path: string, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
    refuse(path, `must be a whole number from ${min} to ${max}`);
  }
  return value;
}

/**
 * Reads a point in sixteenths of a map.
 *
 * @param value The value.
 * @param path The field's path.
 * @returns The point.
 * @throws Refusal when it is malformed.
 */
function asSpawn(value: unknown, path: string): { readonly x: number; readonly y: number } {
  const object = asObject(value, path);
  return { x: asWhole(object.x, `${path}.x`, 0, MAX_LOCAL_SIXTEENTHS), y: asWhole(object.y, `${path}.y`, 0, MAX_LOCAL_SIXTEENTHS) };
}

/**
 * Reads a shrine.
 *
 * @param value The value.
 * @param path The field's path.
 * @returns The shrine.
 * @throws Refusal when it is malformed.
 */
function asShrine(value: unknown, path: string): ShrineIndexShrine {
  const o = asObject(value, path);
  return {
    id: asString(o.id, `${path}.id`, 1, MAX_INDEX_ID_CHARS),
    name: asString(o.name, `${path}.name`, 0, MAX_INDEX_NAME_CHARS),
    x: asWhole(o.x, `${path}.x`, 0, MAX_LOCAL_SIXTEENTHS),
    y: asWhole(o.y, `${path}.y`, 0, MAX_LOCAL_SIXTEENTHS),
    standX: asWhole(o.standX, `${path}.standX`, 0, MAX_LOCAL_SIXTEENTHS),
    standY: asWhole(o.standY, `${path}.standY`, 0, MAX_LOCAL_SIXTEENTHS),
  };
}

/**
 * Reads a map.
 *
 * @param value The value.
 * @param path The field's path.
 * @param seen The map ids read so far, added to.
 * @returns The map.
 * @throws Refusal when it is malformed.
 */
function asMap(value: unknown, path: string, seen: Set<string>): ShrineIndexMap {
  const o = asObject(value, path);
  const id = asString(o.id, `${path}.id`, 1, MAX_INDEX_ID_CHARS);
  if (seen.has(id)) {
    refuse(`${path}.id`, "must be unique");
  }
  seen.add(id);
  const name = asString(o.name, `${path}.name`, 0, MAX_INDEX_NAME_CHARS);
  let place: WorldPlace | null = null;
  if (o.place !== null) {
    const p = asObject(o.place, `${path}.place`);
    place = { x: asWhole(p.x, `${path}.place.x`, -PLACE_LIMIT_TILES, PLACE_LIMIT_TILES), y: asWhole(p.y, `${path}.place.y`, -PLACE_LIMIT_TILES, PLACE_LIMIT_TILES) };
  }
  const spawn = o.spawn === null ? null : asSpawn(o.spawn, `${path}.spawn`);
  if (!Array.isArray(o.shrines) || o.shrines.length > MAX_SHRINES) {
    refuse(`${path}.shrines`, `must be a list of at most ${MAX_SHRINES}`);
  }
  const ids = new Set<string>();
  const shrines = o.shrines.map((entry: unknown, i: number) => {
    const shrine = asShrine(entry, `${path}.shrines[${i}]`);
    if (ids.has(shrine.id)) {
      refuse(`${path}.shrines[${i}].id`, "must be unique");
    }
    ids.add(shrine.id);
    return shrine;
  });
  let start: string | null = null;
  if (o.start !== null) {
    start = asString(o.start, `${path}.start`, 1, MAX_INDEX_ID_CHARS);
    if (!ids.has(start)) {
      refuse(`${path}.start`, "must name a shrine of the map");
    }
  }
  return { id, name, place, spawn, start, shrines };
}

/**
 * Reads the world's shrine index, or the absence of one.
 *
 * @param raw The parsed JSON; undefined or null for a version compiled before the index existed.
 * @param homeMapId The home map's id, used only when the index is absent.
 * @returns The index, or the error naming the field refused. An absent index reads as one unplaced home map with no
 *   shrines. Never throws.
 */
export function readShrineIndex(raw: unknown, homeMapId = "main"): ShrineIndexRead {
  if (raw === undefined || raw === null) {
    return { ok: true, index: { schema: 1, home: homeMapId, maps: [{ id: homeMapId, name: "", place: null, spawn: null, start: null, shrines: [] }] } };
  }
  try {
    const o = asObject(raw, "index");
    if (o.schema !== SHRINE_INDEX_SCHEMA) {
      refuse("schema", `must be ${SHRINE_INDEX_SCHEMA}`);
    }
    const home = asString(o.home, "home", 1, MAX_INDEX_ID_CHARS);
    if (!Array.isArray(o.maps)) {
      refuse("maps", "must be a list");
    }
    const seen = new Set<string>();
    const maps = o.maps.map((entry: unknown, i: number) => asMap(entry, `maps[${i}]`, seen));
    if (!seen.has(home)) {
      refuse("home", "must be the id of a map in maps");
    }
    return { ok: true, index: { schema: 1, home, maps } };
  } catch (error) {
    return { ok: false, error: error instanceof Refusal ? error.message : "index: unreadable" };
  }
}

/**
 * The shrines a character knows on maps other than the target, for the target's handoff (ADR-106 decision 12): the
 * character's rows that the index still holds on placed maps, plus the home map's starting shrine by rule, at most
 * `MAX_KNOWN_ELSEWHERE`, the nearest to the target kept. An unplaced target, an unplaced source map, a removed map, a
 * shrine the index no longer holds and a duplicate row each contribute nothing.
 *
 * @param index The index.
 * @param rows The character's discovered shrines.
 * @param targetMapId The map the character is handed to.
 * @param centre The point distance is kept from, in world sixteenths; the target's placement when omitted.
 * @returns The list, nearest first with the ties of decision 5, so the same input gives the same list.
 */
export function knownElsewhere(index: ShrineIndex, rows: readonly KnownRow[], targetMapId: string, centre?: { readonly x: number; readonly y: number }): KnownShrine[] {
  const target = index.maps.find((map) => map.id === targetMapId);
  if (target === undefined || target.place === null) {
    return [];
  }
  const origin = centre ?? worldPosition(target.place, 0, 0);
  const wanted = new Set<string>();
  for (const row of rows) {
    wanted.add(`${row.mapId}\u0000${row.shrineId}`);
  }
  const home = index.maps.find((map) => map.id === index.home);
  if (home !== undefined && home.start !== null) {
    wanted.add(`${home.id}\u0000${home.start}`);
  }
  const found: { readonly known: KnownShrine; readonly distance: number }[] = [];
  for (const map of index.maps) {
    if (map.id === targetMapId || map.place === null) {
      continue;
    }
    for (const shrine of map.shrines) {
      if (!wanted.has(`${map.id}\u0000${shrine.id}`)) {
        continue;
      }
      const at = worldPosition(map.place, shrine.x, shrine.y);
      found.push({
        known: { mapId: map.id, mapName: map.name, shrineId: shrine.id, name: shrine.name, x: at.x, y: at.y, standX: shrine.standX, standY: shrine.standY },
        distance: worldDistance(at.x, at.y, origin.x, origin.y),
      });
    }
  }
  found.sort((a, b) =>
    compareShrineOrder(
      { distance: a.distance, ownMap: false, worldX: a.known.x, worldY: a.known.y, mapId: a.known.mapId, shrineId: a.known.shrineId },
      { distance: b.distance, ownMap: false, worldX: b.known.x, worldY: b.known.y, mapId: b.known.mapId, shrineId: b.known.shrineId },
    ),
  );
  return found.slice(0, MAX_KNOWN_ELSEWHERE).map((entry) => entry.known);
}
